import { Link } from "react-router-dom";
import { useList, fmtRM } from "@/lib/api";
import { PageHeader, StatCard, StatusBadge, Spinner, EmptyState } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  GraduationCap, Users, Music2, Building2, Wallet, AlertCircle, BadgeCheck, Inbox, Plus, Receipt,
} from "lucide-react";
import { dayName } from "@/lib/api";
import TodayGlance from "./TodayGlance";

export default function AdminDashboard() {
  const { data, loading } = useList("/dashboard/admin");
  if (loading || !data) return <Spinner full />;
  const s = data.stats;

  return (
    <div data-testid="admin-dashboard">
      <PageHeader
        title="Dashboard"
        description={`Operations overview for ${data.month}`}
        actions={
          <>
            <Button asChild variant="outline" data-testid="quick-add-student">
              <Link to="/admin/students/new"><Plus className="h-4 w-4 mr-1" />Student</Link>
            </Button>
            <Button asChild data-testid="quick-invoices">
              <Link to="/admin/invoices"><Receipt className="h-4 w-4 mr-1" />Invoices</Link>
            </Button>
          </>
        }
      />
      <TodayGlance />
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4 stagger-in">
        <StatCard icon={Wallet} label="Collected this month" value={fmtRM(s.collected_this_month)} accent testId="stat-collected" />
        <StatCard icon={AlertCircle} label="Outstanding" value={fmtRM(s.outstanding_this_month)} sub={`${s.invoices_this_month} invoices issued`} testId="stat-outstanding" />
        <StatCard icon={GraduationCap} label="Active students" value={s.students_active} testId="stat-students" />
        <StatCard icon={Users} label="Active teachers" value={s.teachers_active} testId="stat-teachers" />
        <StatCard icon={Music2} label="Courses" value={s.courses} testId="stat-courses" />
        <StatCard icon={Building2} label="Studios" value={s.studios} testId="stat-studios" />
        <StatCard icon={BadgeCheck} label="Pending approvals" value={s.pending_reschedules + s.pending_payrolls}
          sub={`${s.pending_reschedules} reschedules · ${s.pending_payrolls} payrolls`} testId="stat-approvals" />
        <StatCard icon={Inbox} label="New enquiries" value={s.new_enquiries} testId="stat-enquiries" />
      </div>

      <div className="mt-10">
        <h3 className="text-xl font-semibold mb-4">Today's classes</h3>
        <div className="rounded-lg border bg-card overflow-x-auto" data-testid="todays-classes-table">
          {data.todays_classes.length === 0 ? (
            <div className="p-6"><EmptyState text="No classes scheduled today." /></div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead>Time</TableHead><TableHead>Student</TableHead><TableHead>Course</TableHead>
                  <TableHead>Teacher</TableHead><TableHead>Studio</TableHead><TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.todays_classes.map((c) => (
                  <TableRow key={c.id} data-testid={`today-class-${c.id}`}>
                    <TableCell className="font-mono text-xs">{c.start_time}–{c.end_time}</TableCell>
                    <TableCell className="font-medium">{c.student_name}</TableCell>
                    <TableCell>{c.course_name}</TableCell>
                    <TableCell>{c.teacher_name}</TableCell>
                    <TableCell>{c.studio_name}</TableCell>
                    <TableCell><StatusBadge status={c.status} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
        <p className="text-xs text-muted-foreground mt-2">{dayName(new Date(data.today + "T00:00:00").getDay() === 0 ? 6 : new Date(data.today + "T00:00:00").getDay() - 1)}, {data.today}</p>
      </div>
    </div>
  );
}
